import { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { usePortfolio, Portfolio } from '@/hooks/usePortfolio';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Wallet,
  Plus,
  DollarSign,
  TrendingUp,
  TrendingDown,
  MoreVertical,
  Trash2,
  Eye,
  PlusCircle,
  MinusCircle,
  RefreshCw,
  Search,
  X,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Loader2, Inbox, Share2, AlertTriangle } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import {
  isValidPortfolioId,
  sanitizeQ,
  sanitizeSide,
  sanitizeExpanded,
  sanitizeShare,
  stripPortfolioParams,
} from './portfolioUrl';

export function PortfolioManager() {
  const {
    portfolios,
    currentPortfolio,
    positions,
    orders,
    loading,
    error,
    fetchPortfolios,
    fetchPortfolioDetails,
    createPortfolio,
    addFunds,
    withdrawFunds,
    deletePortfolio,
  } = usePortfolio();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showFundsModal, setShowFundsModal] = useState<'add' | 'withdraw' | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState<string | null>(null);
  const [selectedPortfolio, setSelectedPortfolio] = useState<Portfolio | null>(null);
  const [viewingDetails, setViewingDetails] = useState(false);
  const POSITION_FILTERS_KEY = 'atp.portfolio.positionFilters.v2';
  const POSITION_FILTERS_LEGACY_KEY = 'atp.portfolio.positionFilters.v1';

  type PositionFilter = { search: string; side: 'all' | 'buy' | 'sell' };
  const DEFAULT_FILTER: PositionFilter = { search: '', side: 'all' };

  const readAllFilters = (): Record<string, PositionFilter> => {
    try {
      // One-time cleanup of the previous global key
      if (localStorage.getItem(POSITION_FILTERS_LEGACY_KEY)) {
        localStorage.removeItem(POSITION_FILTERS_LEGACY_KEY);
      }
      const raw = localStorage.getItem(POSITION_FILTERS_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  };

  const readFilters = (portfolioId: string): PositionFilter => {
    const all = readAllFilters();
    const entry = all[portfolioId];
    if (!entry) return { ...DEFAULT_FILTER };
    return {
      search: typeof entry.search === 'string' ? entry.search : '',
      side: entry.side === 'buy' || entry.side === 'sell' ? entry.side : 'all',
    };
  };

  const writeFilters = (portfolioId: string, value: PositionFilter) => {
    try {
      const all = readAllFilters();
      all[portfolioId] = value;
      localStorage.setItem(POSITION_FILTERS_KEY, JSON.stringify(all));
    } catch {
      /* storage unavailable — ignore */
    }
  };

  const [positionSearch, setPositionSearch] = useState<string>('');
  const [positionSideFilter, setPositionSideFilter] = useState<'all' | 'buy' | 'sell'>('all');
  const [filtersExpanded, setFiltersExpanded] = useState<boolean>(false);
  const hydratedPortfolioIdRef = useRef<string | null>(null);

  // URL <-> state sync for shareable deep links
  const [searchParams, setSearchParams] = useSearchParams();
  const urlAppliedRef = useRef<string | null>(null); // tracks which portfolio id has had URL overrides applied
  const notFoundShownRef = useRef<string | null>(null); // tracks which missing portfolio id we've already toasted
  const shareAutoOpenedRef = useRef<string | null>(null); // tracks which portfolio id has auto-opened the share modal
  // Debounce window so rapid back/forward navigation through several malformed URLs
  // never spams duplicate "invalid link" toasts. We always still scrub the URL —
  // the banner below is the persistent recovery surface.
  const lastInvalidToastRef = useRef<{ id: string | null; at: number }>({
    id: null,
    at: 0,
  });
  const INVALID_TOAST_DEBOUNCE_MS = 1500;

  // Inline banner state — explains why the link was invalid and offers a way back.
  const [invalidLinkBanner, setInvalidLinkBanner] = useState<{ id: string } | null>(
    null,
  );
  // Mirror banner for the "valid uuid but no longer exists" path.
  const [missingLinkBanner, setMissingLinkBanner] = useState<{ id: string } | null>(
    null,
  );

  // Truncate id for display so absurd URL inputs don't blow out the banner row.
  const displayBannerId = (raw: string) =>
    raw.length > 64 ? `${raw.slice(0, 64)}…` : raw;

  // Hydrate filters whenever the selected portfolio changes (covers every selection path).
  // URL params (q, side, expanded) override saved values on first land for a given portfolio id;
  // unknown/invalid values fall back safely to saved or defaults.
  useEffect(() => {
    const id = selectedPortfolio?.id ?? null;
    if (!id) {
      setPositionSearch('');
      setPositionSideFilter('all');
      setFiltersExpanded(false);
      hydratedPortfolioIdRef.current = null;
      urlAppliedRef.current = null;
      shareAutoOpenedRef.current = null;
      return;
    }
    const saved = readFilters(id);
    const urlPortfolio = searchParams.get('portfolio');
    const urlQ = sanitizeQ(searchParams.get('q'));
    const urlSide = sanitizeSide(searchParams.get('side'));
    const hasExpandedParam = searchParams.has('expanded');
    const applyUrl =
      urlPortfolio === id &&
      urlAppliedRef.current !== id &&
      (urlQ !== null || urlSide !== null || hasExpandedParam);

    setPositionSearch(applyUrl && urlQ !== null ? urlQ : saved.search);
    setPositionSideFilter(applyUrl && urlSide !== null ? urlSide : saved.side);
    setFiltersExpanded(
      applyUrl && hasExpandedParam ? sanitizeExpanded(searchParams.get('expanded')) : false,
    );
    hydratedPortfolioIdRef.current = id;
    if (applyUrl) urlAppliedRef.current = id;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPortfolio?.id]);

  // Persist filter changes — but only after hydration has run for this portfolio,
  // so we never overwrite saved values with the initial defaults on the same tick.
  useEffect(() => {
    const id = selectedPortfolio?.id;
    if (!id) return;
    if (hydratedPortfolioIdRef.current !== id) return;
    writeFilters(id, {
      search: positionSearch,
      side: positionSideFilter,
    });
  }, [selectedPortfolio?.id, positionSearch, positionSideFilter]);

  // Mirror state -> URL so the address bar always reflects current selection + filters
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const id = selectedPortfolio?.id;

    if (!id) {
      let changed = false;
      ['portfolio', 'q', 'side', 'expanded'].forEach((k) => {
        if (next.has(k)) { next.delete(k); changed = true; }
      });
      if (changed) setSearchParams(next, { replace: true });
      return;
    }

    const hasActiveFilter =
      positionSearch.trim() !== '' || positionSideFilter !== 'all';

    const desired: Record<string, string | null> = {
      portfolio: id,
      q: positionSearch.trim() !== '' ? positionSearch : null,
      side: positionSideFilter !== 'all' ? positionSideFilter : null,
      // Only emit expanded when it's true AND there is something to expand
      expanded: filtersExpanded && hasActiveFilter ? '1' : null,
    };

    let changed = false;
    Object.entries(desired).forEach(([k, v]) => {
      const cur = next.get(k);
      if (v === null) {
        if (cur !== null) { next.delete(k); changed = true; }
      } else if (cur !== v) {
        next.set(k, v);
        changed = true;
      }
    });
    if (changed) setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPortfolio?.id, positionSearch, positionSideFilter, filtersExpanded]);

  // Pre-flight URL validation: reject malformed ?portfolio=<id> values immediately
  // (without waiting for portfolios to load). Always strips related params and
  // surfaces an inline banner; the toast is debounced so rapid back/forward
  // navigation through several bad URLs never fires duplicate messages.
  useEffect(() => {
    const urlPortfolio = searchParams.get('portfolio');
    if (!urlPortfolio) {
      // URL is clean again — let the banner clear itself if it's still around.
      return;
    }
    if (isValidPortfolioId(urlPortfolio)) return;

    // Always show the recoverable banner (truncate id to keep the row sane).
    const rawId: string = urlPortfolio as string;
    setInvalidLinkBanner({ id: displayBannerId(rawId) });

    // Debounced toast: any second invalid-link toast inside the window is suppressed.
    const now = Date.now();
    const last = lastInvalidToastRef.current;
    if (now - last.at >= INVALID_TOAST_DEBOUNCE_MS) {
      toast.error('Invalid portfolio link — the address has been cleaned up.');
      lastInvalidToastRef.current = { id: rawId, at: now };
    }

    const { next, changed } = stripPortfolioParams(searchParams);
    if (changed) setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Auto-open drawer when a ?portfolio=<id> URL lands and the list is loaded.
  // If the id doesn't match any portfolio after loading completes, surface a one-time
  // error + recoverable banner and clear the stale params so the URL stays trustworthy.
  useEffect(() => {
    const urlPortfolio = searchParams.get('portfolio');
    if (!urlPortfolio) return;
    if (!isValidPortfolioId(urlPortfolio)) return; // malformed ids are owned by the pre-flight effect above
    if (selectedPortfolio?.id === urlPortfolio) return;
    if (!portfolios) return;
    const match = portfolios.find((p) => p.id === urlPortfolio);
    if (match) {
      setSelectedPortfolio(match);
      setViewingDetails(true);
      fetchPortfolioDetails(match.id);
      // A real portfolio took over — drop any stale link banners.
      setInvalidLinkBanner(null);
      setMissingLinkBanner(null);
      return;
    }
    if (loading) return;
    if (notFoundShownRef.current === urlPortfolio) return;
    notFoundShownRef.current = urlPortfolio;
    toast.error('Shared portfolio not found or no longer available.');
    setMissingLinkBanner({ id: displayBannerId(urlPortfolio) });
    const { next, changed } = stripPortfolioParams(searchParams);
    if (changed) setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, portfolios, loading]);

  // ---- Share-link preview modal ------------------------------------------
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  // Build the URL captured into the modal so the *copied* link includes share=1,
  // making the recipient land directly in the share preview modal.
  const buildShareUrl = (): string => {
    if (typeof window === 'undefined') return '';
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('share', '1');
      return url.toString();
    } catch {
      return window.location.href;
    }
  };
  const openShareModal = () => {
    setShareUrl(buildShareUrl());
  };
  const copyShareLink = async () => {
    const url = shareUrl ?? buildShareUrl();
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        toast.success('Share link copied');
        setShareUrl(null);
      } else {
        toast.message('Copy this link manually', { description: url });
      }
    } catch {
      toast.message('Copy this link manually', { description: url });
    }
  };

  // Auto-open the share modal when a recipient lands with ?share=1.
  // Consume the flag from the live URL so refreshing after closing won't reopen it;
  // the captured shareUrl was snapshotted (with share=1) before this strip.
  useEffect(() => {
    const id = selectedPortfolio?.id;
    if (!id) return;
    if (!sanitizeShare(searchParams.get('share'))) return;
    if (shareAutoOpenedRef.current === id) return;
    shareAutoOpenedRef.current = id;
    openShareModal();
    const next = new URLSearchParams(searchParams);
    next.delete('share');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPortfolio?.id, searchParams]);

  // Create form state
  const [newPortfolioName, setNewPortfolioName] = useState('');
  const [newPortfolioType, setNewPortfolioType] = useState<'demo' | 'real'>('demo');
  const [newPortfolioBalance, setNewPortfolioBalance] = useState(10000);

  // Funds form state
  const [fundsAmount, setFundsAmount] = useState(1000);

  useEffect(() => {
    fetchPortfolios();
  }, [fetchPortfolios]);

  const handleCreatePortfolio = async () => {
    if (!newPortfolioName.trim()) {
      toast.error('Portfolio name is required');
      return;
    }

    try {
      await createPortfolio(newPortfolioName, newPortfolioType, newPortfolioBalance);
      toast.success('Portfolio created');
      setShowCreateModal(false);
      setNewPortfolioName('');
      setNewPortfolioBalance(10000);
    } catch (err) {
      toast.error('Failed to create portfolio');
    }
  };

  const handleAddFunds = async () => {
    if (!selectedPortfolio || fundsAmount <= 0) return;

    try {
      await addFunds(selectedPortfolio.id, fundsAmount);
      toast.success(`$${fundsAmount.toLocaleString()} added`);
      setShowFundsModal(null);
      setFundsAmount(1000);
    } catch (err) {
      toast.error('Failed to add funds');
    }
  };

  const handleWithdraw = async () => {
    if (!selectedPortfolio || fundsAmount <= 0) return;

    try {
      await withdrawFunds(selectedPortfolio.id, fundsAmount);
      toast.success(`$${fundsAmount.toLocaleString()} withdrawn`);
      setShowFundsModal(null);
      setFundsAmount(1000);
    } catch (err) {
      toast.error('Failed to withdraw funds');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deletePortfolio(id);
      toast.success('Portfolio deleted');
      setShowDeleteDialog(null);
    } catch (err) {
      toast.error('Failed to delete portfolio');
    }
  };

  const handleViewDetails = (portfolio: Portfolio) => {
    // Filter hydration is handled by the effect keyed on selectedPortfolio.id.
    setSelectedPortfolio(portfolio);
    setViewingDetails(true);
    fetchPortfolioDetails(portfolio.id);
  };

  const closeDetails = () => {
    setViewingDetails(false);
    if (!showFundsModal) setSelectedPortfolio(null);
  };

  const filteredPositions = useMemo(() => {
    if (!positions) return [];
    const q = positionSearch.trim().toLowerCase();
    return positions.filter((p) => {
      if (positionSideFilter !== 'all' && p.side !== positionSideFilter) return false;
      if (q && !p.symbol.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [positions, positionSearch, positionSideFilter]);

  const fmtNum = (n: number | null | undefined, dp = 2) =>
    Number.isFinite(Number(n)) ? Number(n).toFixed(dp) : '—';
  const fmtPnl = (n: number | null | undefined) => {
    const v = Number(n ?? 0);
    const sign = v > 0 ? '+' : v < 0 ? '' : '';
    return `${sign}${v.toFixed(2)}`;
  };
  const pnlClass = (n: number | null | undefined) => {
    const v = Number(n ?? 0);
    if (v > 0) return 'text-emerald-400';
    if (v < 0) return 'text-red-400';
    return 'text-muted-foreground';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Portfolio Manager</h2>
          <p className="text-sm text-muted-foreground">Manage your real and demo portfolios</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Create Portfolio
        </Button>
      </div>

      {/* Invalid-link banner — shown when a malformed ?portfolio= UUID was cleaned. */}
      {invalidLinkBanner && (
        <div
          role="alert"
          data-testid="invalid-link-banner"
          className="flex items-start justify-between gap-3 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-4 w-4 mt-0.5 text-red-400 shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-red-300">
                That portfolio link looks malformed.
              </p>
              <p className="text-muted-foreground mt-0.5">
                The id{' '}
                <span className="font-mono">{invalidLinkBanner.id}</span> isn't a
                valid portfolio identifier, so we cleaned it out of the URL.
              </p>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setInvalidLinkBanner(null)}
          >
            Back to my portfolios
          </Button>
        </div>
      )}

      {/* Missing-link banner — shown when a valid UUID points at a portfolio that no longer exists. */}
      {missingLinkBanner && (
        <div
          role="status"
          data-testid="missing-link-banner"
          className="flex items-start justify-between gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-4 w-4 mt-0.5 text-amber-400 shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-amber-300">
                That portfolio isn't available anymore.
              </p>
              <p className="text-muted-foreground mt-0.5">
                The shared link pointed at{' '}
                <span className="font-mono">{missingLinkBanner.id}</span>, which no
                longer exists in your account. We've cleaned the URL so it stops
                trying to load.
              </p>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setMissingLinkBanner(null)}
          >
            Back to my portfolios
          </Button>
        </div>
      )}

      {/* Portfolio Grid */}
      {loading && portfolios.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : portfolios.length === 0 ? (
        <Card className="bg-muted/30 border-dashed">
          <CardContent className="p-12 text-center">
            <Wallet className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Portfolios Yet</h3>
            <p className="text-muted-foreground mb-4">
              Create a demo portfolio to start testing your strategies
            </p>
            <Button onClick={() => setShowCreateModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Portfolio
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {portfolios.map(portfolio => (
            <Card key={portfolio.id} className="bg-card/50 border-border/50">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${portfolio.portfolio_type === 'demo' ? 'bg-blue-500/10' : 'bg-amber-500/10'}`}>
                      <Wallet className={`h-5 w-5 ${portfolio.portfolio_type === 'demo' ? 'text-blue-500' : 'text-amber-500'}`} />
                    </div>
                    <div>
                      <CardTitle className="text-base">{portfolio.portfolio_name}</CardTitle>
                      <Badge variant="outline" className={`text-xs mt-1 ${
                        portfolio.portfolio_type === 'demo'
                          ? 'bg-blue-500/10 text-blue-500 border-blue-500/20'
                          : 'bg-amber-500/10 text-amber-500 border-amber-500/20'
                      }`}>
                        {portfolio.portfolio_type.toUpperCase()}
                      </Badge>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleViewDetails(portfolio)}>
                        <Eye className="h-4 w-4 mr-2" />
                        View Details
                      </DropdownMenuItem>
                      {portfolio.portfolio_type === 'demo' && (
                        <>
                          <DropdownMenuItem onClick={() => {
                            setSelectedPortfolio(portfolio);
                            setShowFundsModal('add');
                          }}>
                            <PlusCircle className="h-4 w-4 mr-2" />
                            Add Funds
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => {
                            setSelectedPortfolio(portfolio);
                            setShowFundsModal('withdraw');
                          }}>
                            <MinusCircle className="h-4 w-4 mr-2" />
                            Withdraw
                          </DropdownMenuItem>
                        </>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={() => setShowDeleteDialog(portfolio.id)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="bg-muted/30 rounded-lg p-3">
                    <p className="text-muted-foreground text-xs">Balance</p>
                    <p className="font-bold text-lg">${Number(portfolio.balance).toLocaleString()}</p>
                  </div>
                  <div className="bg-muted/30 rounded-lg p-3">
                    <p className="text-muted-foreground text-xs">Equity</p>
                    <p className="font-bold text-lg">${Number(portfolio.equity).toLocaleString()}</p>
                  </div>
                  <div className="bg-muted/30 rounded-lg p-3">
                    <p className="text-muted-foreground text-xs">Margin Used</p>
                    <p className="font-medium text-amber-500">${Number(portfolio.margin_used).toLocaleString()}</p>
                  </div>
                  <div className="bg-muted/30 rounded-lg p-3">
                    <p className="text-muted-foreground text-xs">Free Margin</p>
                    <p className="font-medium text-green-500">${Number(portfolio.margin_available).toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Portfolio Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Portfolio</DialogTitle>
            <DialogDescription>
              Create a new demo or real portfolio
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Portfolio Name</Label>
              <Input
                placeholder="My Portfolio"
                value={newPortfolioName}
                onChange={e => setNewPortfolioName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Portfolio Type</Label>
              <Select value={newPortfolioType} onValueChange={(v: 'demo' | 'real') => setNewPortfolioType(v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="demo">Demo (Virtual funds)</SelectItem>
                  <SelectItem value="real">Real (Connect broker)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {newPortfolioType === 'demo' && (
              <div className="space-y-2">
                <Label>Initial Balance ($)</Label>
                <Input
                  type="number"
                  value={newPortfolioBalance}
                  onChange={e => setNewPortfolioBalance(Number(e.target.value))}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreatePortfolio} disabled={loading}>
              {loading ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Withdraw Funds Modal */}
      <Dialog open={showFundsModal !== null} onOpenChange={() => setShowFundsModal(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {showFundsModal === 'add' ? 'Add Funds' : 'Withdraw Funds'}
            </DialogTitle>
            <DialogDescription>
              {showFundsModal === 'add'
                ? 'Add virtual funds to your demo portfolio'
                : 'Withdraw funds from your demo portfolio'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Amount ($)</Label>
              <Input
                type="number"
                value={fundsAmount}
                onChange={e => setFundsAmount(Number(e.target.value))}
              />
            </div>
            {selectedPortfolio && (
              <p className="text-sm text-muted-foreground">
                Current balance: ${Number(selectedPortfolio.balance).toLocaleString()}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFundsModal(null)}>
              Cancel
            </Button>
            <Button
              onClick={showFundsModal === 'add' ? handleAddFunds : handleWithdraw}
              disabled={loading || fundsAmount <= 0}
            >
              {loading ? 'Processing...' : showFundsModal === 'add' ? 'Add Funds' : 'Withdraw'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog !== null} onOpenChange={() => setShowDeleteDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Portfolio</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this portfolio? All positions and orders will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => showDeleteDialog && handleDelete(showDeleteDialog)}
              className="bg-destructive text-destructive-foreground"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Share-link preview modal */}
      <Dialog open={shareUrl !== null} onOpenChange={(open) => { if (!open) setShareUrl(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Share this view</DialogTitle>
            <DialogDescription>
              Opens this portfolio with the same filters and drawer state.
            </DialogDescription>
          </DialogHeader>
          {shareUrl !== null && (() => {
            let params: URLSearchParams;
            try {
              params = new URL(shareUrl).searchParams;
            } catch {
              params = new URLSearchParams();
            }
            const previewPortfolio = params.get('portfolio');
            const previewQ = params.get('q');
            const previewSide = params.get('side');
            const previewExpanded = params.get('expanded') === '1';
            const dash = <span className="text-muted-foreground">—</span>;
            return (
              <div className="space-y-3">
                <Input
                  readOnly
                  value={shareUrl}
                  onFocus={(e) => e.currentTarget.select()}
                  className="font-mono text-xs"
                  aria-label="Share link"
                />
                <div className="border border-border/60 divide-y divide-border/60 text-xs">
                  <div className="grid grid-cols-3 gap-2 px-3 py-2">
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-display">Portfolio</span>
                    <span className="col-span-2 font-mono truncate">{previewPortfolio ?? dash}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 px-3 py-2">
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-display">Search</span>
                    <span className="col-span-2 font-mono truncate">{previewQ ?? dash}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 px-3 py-2">
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-display">Side</span>
                    <span
                      className={`col-span-2 text-[10px] uppercase tracking-wider font-display ${
                        previewSide === 'buy'
                          ? 'text-emerald-400'
                          : previewSide === 'sell'
                          ? 'text-red-400'
                          : ''
                      }`}
                    >
                      {previewSide ?? dash}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 px-3 py-2">
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-display">Filters</span>
                    <span className="col-span-2 text-[10px] uppercase tracking-wider font-display text-muted-foreground">
                      {previewExpanded ? 'Expanded' : 'Collapsed'}
                    </span>
                  </div>
                </div>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShareUrl(null)}>Cancel</Button>
            <Button onClick={copyShareLink}>
              <Share2 className="h-4 w-4 mr-1.5" />
              Copy link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Portfolio Detail Drawer */}
      <Sheet open={viewingDetails} onOpenChange={(open) => { if (!open) closeDetails(); }}>
        <SheetContent side="right" className="w-full sm:max-w-[480px] overflow-y-auto">
          <SheetHeader className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <SheetTitle className="font-display text-lg">
                {selectedPortfolio?.portfolio_name ?? 'Portfolio'}
              </SheetTitle>
              {selectedPortfolio && (
                <Badge
                  variant="outline"
                  className={`text-xs uppercase tracking-wide ${
                    selectedPortfolio.portfolio_type === 'real'
                      ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                      : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                  }`}
                >
                  {selectedPortfolio.portfolio_type}
                </Badge>
              )}
            </div>
            <SheetDescription className="text-xs text-muted-foreground">
              Live snapshot of balances, open positions, and recent orders.
            </SheetDescription>
          </SheetHeader>

          {selectedPortfolio && (
            <div className="mt-6 space-y-6">
              {/* Summary grid */}
              <div className="grid grid-cols-2 gap-3">
                <div className="border border-border/60 p-3">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Balance</p>
                  <p className="font-mono text-base mt-1">
                    {selectedPortfolio.currency} {fmtNum(currentPortfolio?.balance ?? selectedPortfolio.balance)}
                  </p>
                </div>
                <div className="border border-border/60 p-3">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Equity</p>
                  <p className="font-mono text-base mt-1">
                    {selectedPortfolio.currency} {fmtNum(currentPortfolio?.equity ?? selectedPortfolio.equity)}
                  </p>
                </div>
                <div className="border border-border/60 p-3">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Margin Used</p>
                  <p className="font-mono text-sm mt-1">
                    {fmtNum(currentPortfolio?.margin_used ?? selectedPortfolio.margin_used)}
                  </p>
                </div>
                <div className="border border-border/60 p-3">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Available</p>
                  <p className="font-mono text-sm mt-1">
                    {fmtNum(currentPortfolio?.margin_available ?? selectedPortfolio.margin_available)}
                  </p>
                </div>
                <div className="border border-border/60 p-3 col-span-2">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Created</p>
                  <p className="font-mono text-xs mt-1">
                    {new Date(selectedPortfolio.created_at).toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Loading */}
              {loading && (
                <div className="flex items-center justify-center py-6 text-muted-foreground text-xs">
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Loading portfolio details…
                </div>
              )}

              {/* Open Positions */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-display">
                    Open Positions
                  </h3>
                  <span className="text-[10px] text-muted-foreground font-mono">
                    {filteredPositions.length} / {positions?.length ?? 0}
                  </span>
                </div>

                {positions && positions.length > 0 && (
                  <div className="flex items-center gap-2 mb-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                      <Input
                        value={positionSearch}
                        onChange={(e) => setPositionSearch(e.target.value)}
                        placeholder="Search symbol..."
                        className="h-8 pl-7 pr-7 text-xs font-mono"
                      />
                      {positionSearch && (
                        <button
                          type="button"
                          onClick={() => setPositionSearch('')}
                          className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-muted-foreground hover:text-foreground"
                          aria-label="Clear search"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                    <div className="flex items-center border border-border/60 divide-x divide-border/60">
                      {(['all', 'buy', 'sell'] as const).map((s) => {
                        const active = positionSideFilter === s;
                        const tone =
                          s === 'buy'
                            ? active ? 'bg-emerald-500/15 text-emerald-400' : 'text-muted-foreground hover:text-emerald-400'
                            : s === 'sell'
                            ? active ? 'bg-red-500/15 text-red-400' : 'text-muted-foreground hover:text-red-400'
                            : active ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground';
                        return (
                          <button
                            key={s}
                            type="button"
                            onClick={() => setPositionSideFilter(s)}
                            className={`px-2.5 h-8 text-[10px] uppercase tracking-wider font-display transition-colors ${tone}`}
                          >
                            {s}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {positions && positions.length > 0 && (positionSearch.trim() !== '' || positionSideFilter !== 'all') && (() => {
                  const activeCount =
                    (positionSearch.trim() !== '' ? 1 : 0) +
                    (positionSideFilter !== 'all' ? 1 : 0);

                  const searchChip = positionSearch.trim() !== '' && (
                    <span className="inline-flex items-center gap-1 h-6 px-2 border border-border/60 bg-secondary/40 text-[10px] font-mono">
                      Search: <span className="text-foreground">{positionSearch}</span>
                      <button
                        type="button"
                        onClick={() => setPositionSearch('')}
                        className="ml-0.5 text-muted-foreground hover:text-foreground"
                        aria-label="Clear search filter"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  );

                  const sideChip = positionSideFilter !== 'all' && (
                    <span
                      className={`inline-flex items-center gap-1 h-6 px-2 border text-[10px] uppercase tracking-wider font-display ${
                        positionSideFilter === 'buy'
                          ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-400'
                          : 'border-red-500/40 bg-red-500/10 text-red-400'
                      }`}
                    >
                      Side: {positionSideFilter}
                      <button
                        type="button"
                        onClick={() => setPositionSideFilter('all')}
                        className="ml-0.5 opacity-70 hover:opacity-100"
                        aria-label="Clear side filter"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  );

                  const resetButton = (
                    <button
                      type="button"
                      onClick={() => {
                        setPositionSearch('');
                        setPositionSideFilter('all');
                      }}
                      className="h-6 px-2 text-[10px] uppercase tracking-wider font-display text-muted-foreground hover:text-foreground border border-border/60 hover:border-border"
                    >
                      Reset
                    </button>
                  );

                  const shareButtonWide = (
                    <button
                      type="button"
                      onClick={openShareModal}
                      className="inline-flex items-center gap-1 h-6 px-2 text-[10px] uppercase tracking-wider font-display text-muted-foreground hover:text-foreground border border-border/60 hover:border-border"
                      aria-label="Copy share link"
                      title="Copy share link"
                    >
                      <Share2 className="h-3 w-3" />
                      Share
                    </button>
                  );

                  const shareButtonNarrow = (
                    <button
                      type="button"
                      onClick={openShareModal}
                      className="inline-flex items-center justify-center h-6 w-6 text-muted-foreground hover:text-foreground border border-border/60 hover:border-border"
                      aria-label="Copy share link"
                      title="Copy share link"
                    >
                      <Share2 className="h-3 w-3" />
                    </button>
                  );

                  return (
                    <>
                      {/* Wide viewport: inline chips */}
                      <div className="hidden sm:flex items-center flex-wrap gap-1.5 mb-2">
                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-display">
                          Active filters
                        </span>
                        {searchChip}
                        {sideChip}
                        <div className="ml-auto flex items-center gap-1.5">
                          {shareButtonWide}
                          {resetButton}
                        </div>
                      </div>

                      {/* Narrow viewport: collapsible pill */}
                      <div className="sm:hidden mb-2">
                        <button
                          type="button"
                          onClick={() => setFiltersExpanded((v) => !v)}
                          className="inline-flex items-center gap-1.5 h-6 px-2 border border-border/60 bg-secondary/40 text-[10px] uppercase tracking-wider font-display text-muted-foreground hover:text-foreground"
                          aria-expanded={filtersExpanded}
                          aria-controls="active-filters-panel"
                        >
                          <span>Active filters · {activeCount}</span>
                          {filtersExpanded ? (
                            <ChevronUp className="h-3 w-3" />
                          ) : (
                            <ChevronDown className="h-3 w-3" />
                          )}
                        </button>
                        {filtersExpanded && (
                          <div
                            id="active-filters-panel"
                            className="mt-1.5 flex flex-wrap items-center gap-1.5"
                          >
                            {searchChip}
                            {sideChip}
                            {shareButtonNarrow}
                            {resetButton}
                          </div>
                        )}
                      </div>
                    </>
                  );
                })()}

                {positions && positions.length > 0 ? (
                  filteredPositions.length > 0 ? (
                    <div className="border border-border/60 divide-y divide-border/60">
                      {filteredPositions.map((p) => {
                        const mv = Number(p.volume) * Number(p.current_price);
                        return (
                          <div key={p.id} className="grid grid-cols-12 gap-2 px-3 py-2 text-xs">
                            <div className="col-span-4">
                              <p className="font-mono">{p.symbol}</p>
                              <p className={`text-[10px] uppercase ${p.side === 'buy' ? 'text-emerald-400' : 'text-red-400'}`}>
                                {p.side}
                              </p>
                            </div>
                            <div className="col-span-3 font-mono text-right">
                              <p>{fmtNum(p.volume, 4)}</p>
                              <p className="text-[10px] text-muted-foreground">@ {fmtNum(p.entry_price, 4)}</p>
                            </div>
                            <div className="col-span-3 font-mono text-right">
                              <p>{fmtNum(mv)}</p>
                              <p className="text-[10px] text-muted-foreground">mkt val</p>
                            </div>
                            <div className={`col-span-2 font-mono text-right ${pnlClass(p.profit_loss)}`}>
                              {fmtPnl(p.profit_loss)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="border border-dashed border-border/60 p-4 text-center text-xs text-muted-foreground flex flex-col items-center gap-2">
                      <Inbox className="h-5 w-5" />
                      <span>No positions match your filters.</span>
                      <button
                        type="button"
                        onClick={() => { setPositionSearch(''); setPositionSideFilter('all'); }}
                        className="text-[10px] uppercase tracking-wider text-primary hover:underline"
                      >
                        Clear filters
                      </button>
                    </div>
                  )
                ) : !loading ? (
                  <div className="border border-dashed border-border/60 p-6 text-center text-xs text-muted-foreground flex flex-col items-center gap-2">
                    <Inbox className="h-5 w-5" />
                    No open positions.
                  </div>
                ) : null}
              </div>

              {/* Recent Orders */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-display">
                    Recent Orders
                  </h3>
                  <span className="text-[10px] text-muted-foreground font-mono">
                    {Math.min(orders?.length ?? 0, 10)}
                  </span>
                </div>
                {orders && orders.length > 0 ? (
                  <div className="border border-border/60 divide-y divide-border/60">
                    {orders.slice(0, 10).map((o) => (
                      <div key={o.id} className="grid grid-cols-12 gap-2 px-3 py-2 text-xs">
                        <div className="col-span-4">
                          <p className="font-mono">{o.symbol}</p>
                          <p className={`text-[10px] uppercase ${o.side === 'buy' ? 'text-emerald-400' : 'text-red-400'}`}>
                            {o.side} · {o.order_type}
                          </p>
                        </div>
                        <div className="col-span-3 font-mono text-right">
                          {fmtNum(o.volume, 4)}
                        </div>
                        <div className="col-span-2 text-right">
                          <Badge variant="outline" className="text-[10px] px-1 py-0 font-mono uppercase">
                            {o.status}
                          </Badge>
                        </div>
                        <div className="col-span-3 font-mono text-[10px] text-right text-muted-foreground">
                          {new Date(o.created_at).toLocaleString(undefined, {
                            month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit',
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : !loading ? (
                  <div className="border border-dashed border-border/60 p-6 text-center text-xs text-muted-foreground flex flex-col items-center gap-2">
                    <Inbox className="h-5 w-5" />
                    No orders yet.
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

export default PortfolioManager;
